import { formatUtcFetchTimestamp } from '@/utils/dateutil';
import { readResidentCategories, saveResidentCategories } from '@/utils/residentCategories';
import React from 'react';
import { css } from '@emotion/react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth } from '../contexts/AuthContext';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useLoading } from '../contexts/LoadingContext';
import { selfDataSelector, selfDataSlice, showMedicalContentSelector } from '../redux/config/selfData';
import { currentNursingHomeSlice } from '../redux/config/currentNursingHome';
import api from '../api';
import dayjs from 'dayjs';
import { LeftNav } from '../components/leftNav';
import { GlobalHeader } from '../components/globalHeader';
import { IGetNursingHomeRes } from '../types/api/nursingHome/getNursingHome';
import { residentListSettingsSlice } from '../redux/config/residentListSettings';
import { navSlice } from '../redux/config/leftnav';
import { AxiosError } from 'axios';
import { getUnreadChatCount } from '../lib/chatUnread';
import { ChatPanel } from '../pages/chat';
import { color } from '../styles';
import { TermsAgreementModal } from '../components/termsAgreementModal';
import { getTermsAccepted, storeTermsAcceptance } from '../utils/termsAgreement';

interface IErrorResponse {
  error: string;
}

// professional, medicalの共通の通知カバー種別を抽出した型
type BadgeResident = {
  bigBladderNotification: boolean;
  gotUpNotification: boolean;
  sensorErrorNotification: boolean;
  upsideDownNotification: boolean;
  urinatedNotification: boolean;
  zeroResetNotification: boolean;
  urineResetNotification: boolean;
};

const BADGE_POLLING_INTERVAL_MS = 1000 * 60 * 5;

const CHAT_PANEL_WIDTH = 'clamp(480px, 42vw, 600px)';

const AppWrapper = (isChatPanelDocked: boolean) => css`
  display: flex;
  font-size: 14px;
  height: 100vh;
  width: 100%;
  padding-right: ${isChatPanelDocked ? CHAT_PANEL_WIDTH : '0'};
  box-sizing: border-box;

  @media (max-width: 900px) {
    padding-right: 0;
  }
`;

const ContentWrapper = css`
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
`;

const ScrollContainer = css`
  width: 100%;
  height: calc(100vh - 60px);
  overflow: auto;
  background-color: var(--surface-content);
`;

const ChatBackdrop = css`
  position: fixed;
  z-index: 1200;
  inset: 0;
  background: rgba(24, 32, 38, 0.3);
`;

const ChatPanelContainer = (isFullscreen: boolean) => css`
  position: fixed;
  z-index: 1201;
  top: ${isFullscreen ? '16px' : '0'};
  right: ${isFullscreen ? '16px' : '0'};
  bottom: ${isFullscreen ? '16px' : '0'};
  left: ${isFullscreen ? '16px' : 'auto'};
  width: ${isFullscreen ? 'auto' : CHAT_PANEL_WIDTH};
  overflow: hidden;
  box-sizing: border-box;
  background: ${color.white};
  box-shadow: ${isFullscreen ? '0 4px 18px rgba(24, 32, 38, 0.24)' : '-12px 0 12px rgba(24, 32, 38, 0.3)'};

  @media (max-width: 900px) {
    top: ${isFullscreen ? '8px' : '0'};
    right: ${isFullscreen ? '8px' : '0'};
    bottom: ${isFullscreen ? '8px' : '0'};
    left: ${isFullscreen ? '8px' : 'auto'};
    width: ${isFullscreen ? 'auto' : 'min(600px, 100vw)'};
    border-top: 0;
    border-right: 0;
    border-bottom: 0;
  }
`;

export const DefaultLayout: React.FC = ({ children }) => {
  const { nursingHomeId } = useParams<{ nursingHomeId: string }>();
  const { selfUser } = useSelector(selfDataSelector);
  const showMedicalContent = useSelector(showMedicalContentSelector);
  const { currentUser } = useAuth();
  const { showLoading, hideLoading } = useLoading();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const isChatRoom = location.pathname.endsWith('/chat');
  const [isOpenLeftnav, setIsOpenLeftnav] = React.useState(true);
  const [isChatPanelOpen, setIsChatPanelOpen] = React.useState(false);
  const [isChatPanelDocked, setIsChatPanelDocked] = React.useState(true);
  const [isChatPanelFullscreen, setIsChatPanelFullscreen] = React.useState(false);
  const isChatActive = isChatRoom || isChatPanelOpen;
  const [nursingHomeList, setNursingHomeList] = React.useState<IGetNursingHomeRes[] | null>(null);
  const [hasAcceptedTerms, setHasAcceptedTerms] = React.useState(getTermsAccepted);

  const fetchChatUnreadBadge = React.useCallback(
    async (currentNursingHomeId = nursingHomeId) => {
      const userUuid = currentUser.user?.uid;
      const targetNursingHomeId = Number(currentNursingHomeId);
      if (!userUuid || !targetNursingHomeId) {
        dispatch(navSlice.actions.setChatUnreadCount(0));
        return;
      }

      try {
        const unreadCount = await getUnreadChatCount({
          nursingHomeId: targetNursingHomeId,
          userUuid,
        });
        dispatch(navSlice.actions.setChatUnreadCount(unreadCount));
      } catch (err) {
        console.warn('Failed to fetch chat unread status', err);
      }
    },
    [currentUser.user?.uid, dispatch, nursingHomeId],
  );

  const fetchBadge = React.useCallback(
    async (
      currentNursingHomeId = nursingHomeId,
      currentUnitId: number | null = selfUser.nursingHomeUnitId ?? null,
      currentServiceType: string = selfUser.serviceType,
    ) => {
      const params = currentUnitId
        ? { nursingHomeId: currentNursingHomeId, nursingHomeUnitId: currentUnitId }
        : { nursingHomeId: currentNursingHomeId };
      const residents: BadgeResident[] = (
        currentServiceType === 'medical'
          ? await api.get('/traversal/md/nursing-homes/:nursingHomeId/residents', { params })
          : await api.get('/traversal/nursing-homes/:nursingHomeId', { params })
      ).residents;
      const careSchedule = await api.get('/nursing-homes/:nursingHomeId/care-schedule', { params });
      const orders = currentServiceType === 'medical'
        ? []
        : await api.get('/nursing-homes/:nursingHomeId/orders', {
            params: { nursingHomeId: currentNursingHomeId, nursingHomeUnitId: currentUnitId, sort: 'desc', limit: 2000 },
          });
      // 48時間以内のアドバイス件数を取得
      const advices = await api.get('/nursing-homes/:nursingHomeId/advices', {
        params: {
          nursingHomeId: currentNursingHomeId,
          nursingHomeUnitId: currentUnitId,
          from: formatUtcFetchTimestamp(dayjs().subtract(48, 'hour')),
          to: formatUtcFetchTimestamp(dayjs()),
          limit: 200,
        },
      });
      const badge = {
        resident: residents.filter(
          (resident) =>
            resident.bigBladderNotification ||
            resident.gotUpNotification ||
            resident.sensorErrorNotification ||
            resident.upsideDownNotification ||
            resident.urinatedNotification ||
            resident.zeroResetNotification ||
            resident.urineResetNotification,
        ).length,
        careSchedule: careSchedule.length,
        temporarilySaved: orders.filter((order) => order.orderStatus === 'temporarilySaved').length,
        advice: advices.length ?? 0,
      };
      dispatch(navSlice.actions.setBadge(badge));
    },
    [nursingHomeId, selfUser.nursingHomeUnitId, selfUser.serviceType],
  );

  const handleSetSelfUser = React.useCallback(async () => {
    if (currentUser.isPending || !currentUser) return;
    try {
      const self = selfUser.id ? selfUser : await api.get('/users/_self');
      if (!selfUser.id) {
        dispatch(selfDataSlice.actions.setSelfUser(self));
      }
      if (self.nursingHomeUnitId) {
        const selfUnit = await api.get('/nursing-home-units/:nursingHomeUnitId', {
          params: { nursingHomeUnitId: self.nursingHomeUnitId },
        });
        dispatch(selfDataSlice.actions.setSelfUnit(selfUnit));
      }
      const units = await api.get('/nursing-homes/:nursingHomeId/nursing-home-units', {
        params: { nursingHomeId },
      });
      dispatch(selfDataSlice.actions.setSelfUnits(units));

      const selfNursingHome = await api.get('/nursing-homes/:nursingHomeId', {
        params: { nursingHomeId: self.nursingHomeId },
      });

      const currentNursingHome = await api.get('/nursing-homes/:nursingHomeId', {
        params: { nursingHomeId },
      });

      // _selfのnursingHomeGroupIdとURLのIdから取得したnursingHomeGroupIdが異なる場合は404画面へ
      if (selfNursingHome.nursingHomeGroupId !== currentNursingHome.nursingHomeGroupId) {
        history.push('/404');
        return;
      } else {
        dispatch(selfDataSlice.actions.setSelfNursingHome(currentNursingHome));
        dispatch(
          currentNursingHomeSlice.actions.setCurrentNursingHome({
            ...currentNursingHome,
          }),
        );
      }

      // メンバー権限の場合は取得しない
      if (self.professionalRole !== 'member') {
        const nursingHomes = await api.get('/nursing-home-groups/:nursingHomeGroupId/nursing-homes', {
          params: { nursingHomeGroupId: currentNursingHome.nursingHomeGroupId },
        });
        setNursingHomeList(nursingHomes);
      }

      const sameUser = Number(localStorage.getItem('self')) === self.id;
      const categories = (sameUser ? readResidentCategories() : []).filter((value) => {
        if (!value.startsWith('unit-')) return true;
        return !self.nursingHomeUnitId && units.some((unit) => value === `unit-${unit.id}`);
      });
      saveResidentCategories(categories);
      localStorage.setItem('self', String(self.id));
      dispatch(residentListSettingsSlice.actions.setFilterCondition({ categories }));
    } catch (err) {
      if (
        (err as AxiosError<IErrorResponse>).response &&
        (err as AxiosError<IErrorResponse>).response?.status === 403
      ) {
        hideLoading();
        history.push('/404');
        return;
      }
    }
  }, [currentUser, selfUser]);

  const handleOpenLeftnav = React.useCallback(() => {
    setIsOpenLeftnav(true);
  }, []);

  const handleCloseLeftnav = React.useCallback(() => {
    setIsOpenLeftnav(false);
  }, []);

  const handleOpenChatPanel = React.useCallback(() => {
    setIsChatPanelOpen(true);
  }, []);

  const handleCloseChatPanel = React.useCallback(() => {
    setIsChatPanelOpen(false);
    setIsChatPanelFullscreen(false);
  }, []);

  const handleToggleChatPanelLayout = React.useCallback(() => {
    setIsChatPanelDocked((current) => !current);
  }, []);

  const handleToggleChatPanelFullscreen = React.useCallback(() => {
    setIsChatPanelFullscreen((current) => !current);
  }, []);

  const handleAgreeToTerms = React.useCallback(() => {
    storeTermsAcceptance();
    setHasAcceptedTerms(true);
  }, []);

  React.useEffect(() => {
    handleSetSelfUser();
  }, [currentUser]);

  React.useEffect(() => {
    if (!selfUser.id) return;

    const fetchBadges = () => {
      fetchBadge(undefined, selfUser.nursingHomeUnitId ?? null, selfUser.serviceType);
      if (currentUser.user?.uid && !isChatActive) {
        fetchChatUnreadBadge();
      }
    };

    if (isChatActive) {
      dispatch(navSlice.actions.setChatUnreadCount(0));
    }

    fetchBadges();
    const timer = setInterval(fetchBadges, BADGE_POLLING_INTERVAL_MS);
    return () => {
      clearInterval(timer);
    };
  }, [
    currentUser.user?.uid,
    dispatch,
    fetchBadge,
    fetchChatUnreadBadge,
    isChatActive,
    selfUser.id,
    selfUser.nursingHomeUnitId,
    selfUser.serviceType,
  ]);

  React.useEffect(() => {
    if (!isChatPanelOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleCloseChatPanel();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleCloseChatPanel, isChatPanelOpen]);

  return (
    <div css={AppWrapper(isChatPanelOpen && isChatPanelDocked && !isChatPanelFullscreen)}>
      {isOpenLeftnav && <LeftNav isOpenLeftnav={isOpenLeftnav} onClose={handleCloseLeftnav} />}
      <div css={ContentWrapper}>
        <GlobalHeader
          nursingHomes={nursingHomeList}
          isOpenLeftnav={isOpenLeftnav}
          onOpen={handleOpenLeftnav}
          onOpenChat={handleOpenChatPanel}
          fetchBadge={fetchBadge}
        />
        {currentUser && <div css={ScrollContainer}>{children}</div>}
      </div>
      {isChatPanelOpen && (
        <>
          <div css={ChatBackdrop} onClick={handleCloseChatPanel} />
          <aside css={ChatPanelContainer(isChatPanelFullscreen)}>
            <ChatPanel
              isDocked={isChatPanelDocked}
              isFullscreen={isChatPanelFullscreen}
              onClose={handleCloseChatPanel}
              onToggleFullscreen={handleToggleChatPanelFullscreen}
              onToggleLayout={handleToggleChatPanelLayout}
            />
          </aside>
        </>
      )}
      <TermsAgreementModal
        isOpen={selfUser.serviceType === 'medical' && !hasAcceptedTerms}
        onAgree={handleAgreeToTerms}
      />
    </div>
  );
};
